import React, { useState, useEffect } from 'react';

/**
 * SetTopicTargetLevelModal Component
 * React implementation that matches the behavior of the original modal
 * Allows setting target levels for topics and managing evaluation requirements
 */
const SetTopicTargetLevelModal = ({ show, onHide, topicToSetTargetLevel, topicQuestionStats, onSave }) => {
  // State management
  const [targetLevel, setTargetLevel] = useState('4');
  const [needEvaluation, setNeedEvaluation] = useState(false);

  /**
   * Convert difficulty number to text
   */
  const getDifficultyText = (difficulty) => {
    switch (parseInt(difficulty)) {
      case 1: return 'Beginner';
      case 2: return 'Advanced Beginner';
      case 3: return 'Competent';
      case 4: return 'Expert';
      default: return 'Unknown';
    }
  };

  /**
   * Get question count for a specific difficulty level
   */
  const getQuestionCountForDifficulty = (difficulty) => {
    if (!topicQuestionStats || topicQuestionStats.length === 0) {
      return 0;
    }
    const stat = topicQuestionStats.find(s => s.Difficulty === parseInt(difficulty));
    return stat ? stat.NumberOfFinalized : 0;
  };

  /**
   * Check if a difficulty level has enough questions (minimum 5)
   */
  const hasEnoughQuestions = (difficulty) => {
    return getQuestionCountForDifficulty(difficulty) >= 5;
  };

  // Update state when props change
  useEffect(() => {
    if (
      topicToSetTargetLevel &&
      topicToSetTargetLevel.targetLevel !== undefined &&
      topicToSetTargetLevel.targetLevel !== null &&
      topicToSetTargetLevel.targetLevel !== ''
    ) {
      setTargetLevel(String(topicToSetTargetLevel.targetLevel));
    }

    if (topicToSetTargetLevel && topicToSetTargetLevel.needEvaluation !== undefined) {
      setNeedEvaluation(!!topicToSetTargetLevel.needEvaluation);
    }
  }, [topicToSetTargetLevel, topicQuestionStats]);

  if (!show) return null;

  /**
   * Handle modal close
   */
  const handleClose = () => {
    onHide();
  };

  /**
   * Handle backdrop click
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  /**
   * Handle save topic target level
   */
  const handleSave = () => {
    // Validate that selected difficulty has enough questions
    if (!hasEnoughQuestions(targetLevel)) {
      alert(`Cannot save: ${getDifficultyText(targetLevel)} level requires at least 5 questions, but only ${getQuestionCountForDifficulty(targetLevel)} questions are available.`);
      return;
    }

    const updatedTopic = {
      ...topicToSetTargetLevel,
      targetLevel: targetLevel,
      needEvaluation: needEvaluation
    };

    if (onSave) {
      onSave(updatedTopic);
    }

    handleClose();
  };

  return (
    <div className="modal fade show react-modal-fix" style={{ display: 'block' }} onClick={handleBackdropClick}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          
          {/* Modal Header */}
          <div className="modal-header">
            <h4 className="modal-title">
              Set Target Level for {topicToSetTargetLevel?.value || topicToSetTargetLevel?.TopicName || 'Topic'}
            </h4>
            <button 
              type="button" 
              className="btn-close" 
              onClick={handleClose}
              aria-label="Close"
            ></button>
          </div>

          {/* Modal Body */}
          <div className="modal-body">

            {/* Questions Available Section */}
            {topicQuestionStats && topicQuestionStats.length > 0 && (
              <div className="row">
                <div className="col-sm-12">
                  <h4>Questions Available</h4>
                </div>

                <div className="col-sm-12">
                  <table className="table">
                    
                    {/* Table Header */}
                    <thead>
                      <tr>
                        <td><b>Difficulty</b></td>
                        {topicQuestionStats.map((questions, index) => (
                          <td key={index}>
                            {getDifficultyText(questions.Difficulty)}
                          </td>
                        ))}
                      </tr>
                    </thead>

                    {/* Table Body */}
                    <tbody>
                      <tr>
                        <td><b>No of questions</b></td>
                        {topicQuestionStats.map((questions, index) => (
                          <td key={index}>
                            <b className={questions.NumberOfFinalized < 5 ? 'red' : ''}>
                              {questions.NumberOfFinalized}
                            </b>
                          </td>
                        ))}
                      </tr>
                    </tbody>

                  </table>
                </div>
              </div>
            )}

            {/* Set Topic Level Form */}
            <form className="row form form-inline">
              <div className="col-sm-12">
                <h4>Set Topic Level</h4>
              </div>

              <div className="col-sm-12">
                <div className="form-group">
                  <select 
                    name="topictargetLevel" 
                    id="topictargetLevel" 
                    className="form-control"
                    value={targetLevel}
                    onChange={(e) => {
                      const selectedLevel = e.target.value;
                      if (hasEnoughQuestions(selectedLevel)) {
                        setTargetLevel(selectedLevel);
                      } else {
                        alert(`${getDifficultyText(selectedLevel)} level is not available. This level requires at least 5 questions, but only ${getQuestionCountForDifficulty(selectedLevel)} questions are available.`);
                        // Reset to previous value
                        e.target.value = targetLevel;
                      }
                    }}
                  >
                    <option value="1" disabled={!hasEnoughQuestions(1)}>
                      Beginner {!hasEnoughQuestions(1) && `(Insufficient questions: ${getQuestionCountForDifficulty(1)})`}
                    </option>
                    <option value="2" disabled={!hasEnoughQuestions(2)}>
                      Advanced Beginner {!hasEnoughQuestions(2) && `(Insufficient questions: ${getQuestionCountForDifficulty(2)})`}
                    </option>
                    <option value="3" disabled={!hasEnoughQuestions(3)}>
                      Competent {!hasEnoughQuestions(3) && `(Insufficient questions: ${getQuestionCountForDifficulty(3)})`}
                    </option>
                    <option value="4" disabled={!hasEnoughQuestions(4)}>
                      Expert {!hasEnoughQuestions(4) && `(Insufficient questions: ${getQuestionCountForDifficulty(4)})`}
                    </option>
                  </select>
                  {!hasEnoughQuestions(targetLevel) && (
                    <div className="alert alert-warning" style={{ marginTop: '10px', marginBottom: '0' }}>
                      <strong>Warning:</strong> The selected difficulty level ({getDifficultyText(targetLevel)}) has only {getQuestionCountForDifficulty(targetLevel)} questions available. At least 5 questions are required. Please select a different difficulty level.
                    </div>
                  )}
                </div>
              </div>
            </form>

            <div className="clearfix"></div>

          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            
            {/* Evaluation Checkbox */}
            <div className="checkbox pull-left">
              <label style={{ color: 'blue' }}>
                <input 
                  type="checkbox" 
                  checked={needEvaluation}
                  onChange={(e) => setNeedEvaluation(e.target.checked)}
                />
                <b> Requires Evaluation</b>
              </label>
            </div>
            
            {/* Save Button */}
            <button 
              className="btn btn-success pull-right" 
              onClick={handleSave}
            >
              Save
            </button>

          </div>

        </div>
      </div>
    </div>
  );
};

export default SetTopicTargetLevelModal; 